import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { normalizeUrgency, URGENCY_META, type Urgency } from "@/lib/urgency";

type Tone = "neutral" | "accent" | "info" | Urgency;

const tones: Record<string, string> = {
  neutral: "bg-surface-sunken text-content-muted ring-1 ring-hairline-strong",
  accent: "bg-accent/15 text-accent ring-1 ring-accent/30",
  info: "bg-info-tint text-info-text ring-1 ring-info-500/25",
  routine: URGENCY_META.routine.badge,
  monitor: URGENCY_META.monitor.badge,
  urgent: URGENCY_META.urgent.badge,
  critical: URGENCY_META.critical.badge,
};

export function Badge({
  tone = "neutral",
  dot = false,
  className,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
        tones[tone],
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden
          className={cn(
            "size-1.5 rounded-full",
            tone in URGENCY_META
              ? URGENCY_META[tone as Urgency].dot
              : "bg-current opacity-60",
          )}
        />
      )}
      {children}
    </span>
  );
}

/**
 * Urgency is never communicated by colour alone — the label always ships with
 * it, so the badge stays readable for colour-blind clinicians and in print.
 */
export function UrgencyBadge({
  level,
  className,
}: {
  level: Urgency;
  className?: string;
}) {
  return (
    <Badge tone={level} dot className={className}>
      {URGENCY_META[level].label}
    </Badge>
  );
}

/**
 * Conversation priority ("Kritis", "Normal", …).
 *
 * Borrows the urgency palette so severity reads the same everywhere, but keeps
 * the source wording: rendering it through the clinical labels turned "Kritis"
 * and "Normal" into the same badge, which made an urgent queue item look
 * routine.
 */
export function PriorityBadge({
  priority,
  className,
}: {
  priority: string | null | undefined;
  className?: string;
}) {
  if (!priority) return <Badge className={className}>—</Badge>;
  return (
    <Badge tone={normalizeUrgency(priority)} dot className={className}>
      {priority}
    </Badge>
  );
}
